import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';

type AlertType = 'success' | 'danger' | null;

interface AlertContextProps {
    alertMessage: string | null;
    alertType: AlertType;
    setAlert: (type: AlertType, message: string | null) => void;
    clearAlert: () => void;
}

const AlertContext = createContext<AlertContextProps | undefined>(undefined);

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [alertType, setAlertType] = useState<AlertType>(null);

    // Memoized so consumers using setAlert in useCallback deps don't see a new
    // reference on every AlertProvider render (which would cause infinite
    // useEffect loops when reload-on-error happens).
    const setAlert = useCallback((type: AlertType, message: string | null) => {
        setAlertMessage(message);
        setAlertType(type);
    }, []);

    const clearAlert = useCallback(() => {
        setAlertMessage(null);
        setAlertType(null);
    }, []);

    return (
        <AlertContext.Provider value={{ alertMessage, alertType, setAlert, clearAlert }}>
            {children}
        </AlertContext.Provider>
    );
};

export const useAlert = (): AlertContextProps => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};
